"use client";

/**
 * BasicMap Component
 * 
 * This component renders a Google Map with custom markers and an info window.
 * It uses the @vis.gl/react-google-maps library to display a map centered at a given location,
 * and marks various nodes (e.g., shops) on the map. It highlights the user's location, 
 * handles map camera movements, and displays an info window for the selected node.
 */
import React, { useState } from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  Pin,
  InfoWindow
} from "@vis.gl/react-google-maps";
import { mapListingDisplayPhone } from "../utils/shopPublicPhone";
import { pinHoursText } from "../utils/savedShopHours";
import { resolveVisitSiteHref } from "../utils/visitSiteHref";


export default function BasicMap({
  nodes = [],
  mapCenter,
  zoom,
  selected,
  onSelect,
  onCameraChange,
  userLocation,
  hoursToday = {}
}) {
  const listingPhone = selected ? mapListingDisplayPhone(selected) : null;
  const visitHref = selected ? resolveVisitSiteHref(selected) : null;
  const hoursLabel = selected ? pinHoursText(selected, hoursToday) : null;

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <div style={{ height: "100%", width: "100%" }}>
        <Map
          center={mapCenter}
          zoom={zoom}
          mapId="dd4aafb42fcaa7a139f3bf81"
          onCameraChanged={(ev) => onCameraChange?.(ev.detail)}
          style={{ height: "100%", width: "100%" }}
        >
          {userLocation && (
            <AdvancedMarker position={userLocation}>
              <Pin background="red" borderColor="white" glyphColor="white" />
            </AdvancedMarker>
          )}

          {nodes.map((shop, index) => {
            const isSelected = selected?.id === shop.id;
            // Parse lat/lng to numbers if they're strings
            const lat = typeof shop.lat === 'number' ? shop.lat : parseFloat(shop.lat);
            const lng = typeof shop.lng === 'number' ? shop.lng : parseFloat(shop.lng);

            // Only render if we have valid coordinates
            return !isNaN(lat) && !isNaN(lng) ? (
              <AdvancedMarker
                key={index}
                position={{ lat, lng }}
                onClick={() => onSelect(shop)}
              >
                <Pin
                  background={isSelected ? "#FF4444" : "white"}
                  borderColor={isSelected ? "#FFD700" : "green"}
                  glyphColor={isSelected ? "white" : "black"}
                  scale={isSelected ? 1.5 : 1.0}
                />
              </AdvancedMarker>
            ) : null;
          })}

          {selected && (
            <InfoWindow
              position={{
                lat: typeof selected.lat === 'number' ? selected.lat : parseFloat(selected.lat),
                lng: typeof selected.lng === 'number' ? selected.lng : parseFloat(selected.lng)
              }}
              onCloseClick={() => onSelect(null)}
            >
              <div style={{ maxWidth: '250px' }}>
                {selected.photo_url && (
                  <img
                    src={selected.photo_url}
                    alt={selected.shop_name}
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      marginBottom: '8px'
                    }}
                  />
                )}
                <strong>{selected.shop_name}</strong><br />
                {selected.address}<br />
                {listingPhone && (
                  <span>
                    Phone:{' '}
                    <a href={listingPhone.href}>{listingPhone.number}</a>
                    <br />
                  </span>
                )}
                Rating: {selected.rating}<br />
                {hoursLabel ? (
                  <span>🕒 {hoursLabel}<br /></span>
                ) : (
                  <span>🕓 Hours unavailable<br /></span>
                )}
                {visitHref ? (
                  <a href={visitHref} target="_blank" rel="noopener noreferrer">
                    Visit Site
                  </a>
                ) : null}
              </div>
            </InfoWindow>
          )}
        </Map>
      </div>
    </APIProvider>
  );
}
